import React, {useMemo, useState} from 'react';
import {List} from 'immutable';

import {Menu} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import {resource} from '@albert-io/json-api-framework/request/builder';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import {
  getClassroomQuery,
  getCoteachersQuery,
  getInvitationsQuery,
  getStudentsQuery
} from 'client/Classrooms/Classroom/Teacher/People/people.queries';
import PeopleContext from 'client/Classrooms/Classroom/Teacher/People/people.context';
import {AddCoteacherModal} from 'client/Classrooms/Classroom/Teacher/People/Coteachers/AddCoteacher/AddCoteacher.react';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

import {EDGE_MENU_POPPER_PROPS} from '../config/menuPopperProps';

// Reuse the legacy roster modals
type StudentModalKey = 'classCode' | 'email' | 'LMS' | 'googleClassroom';
type ModalKey = StudentModalKey | 'coteacher' | '';

type InviteClassroom = ClassroomModelV1 & {
  getSchool: () => {getId: () => string} | null;
  getStudents: () => List<unknown> | undefined;
};

const studentModals = manageRosterModalCollection as Record<
  StudentModalKey,
  React.ComponentType<{classroom: ClassroomModelV1; handleClose: () => void}>
>;

// Co-teacher parity gate: only teachers approved on the class's school may add co-teachers
const getApprovedPersonnelQuery = (schoolId: string, teacherId: string) =>
  resource('school_personnel_v1')
    .mandarkEndpoint(['school_personnel_v1'])
    .filter({school_v5: schoolId, teacher_v1: teacherId});

const TypedManageRosterContext = ManageRosterContext as unknown as React.Context<unknown>;
const TypedPeopleContext = PeopleContext as unknown as React.Context<unknown>;

interface Props {
  classroomId: string;
  classroom: ClassroomModelV1;
  /** The on-color Invite button; rendered as the menu trigger so the header owns its styling. */
  trigger: React.ComponentProps<typeof Menu.Trigger>['children'];
}

export const InviteMenu = ({classroomId, classroom: initialClassroom, trigger}: Props) => {
  const [modalToShow, setModalToShow] = useState<ModalKey>('');
  // Local copy so "Display class code → Generate a new code" can reflect the new code in place.
  const [classroom, setClassroom] = useState(initialClassroom);

  const queries = useMemo(
    () => ({
      classroom: getClassroomQuery(classroomId),
      invitations: getInvitationsQuery(classroomId),
      students: getStudentsQuery(classroomId)
    }),
    [classroomId]
  );

  const rosterContext = useMemo(
    () => ({
      classroom,
      queries,
      students: (classroom as InviteClassroom).getStudents() ?? List(),
      setIsReady: () => {},
      setClassroom
    }),
    [classroom, queries]
  );

  const schoolId = (classroom as InviteClassroom).getSchool()?.getId() ?? '';
  const teacherId = masqueradeStore.getUserIdByMasqueradeState() ?? '';

  const personnelQuery = useMemo(
    () => getApprovedPersonnelQuery(schoolId, teacherId),
    [schoolId, teacherId]
  );
  const {isReady: isPersonnelReady} = useAreLatestQueriesReady([personnelQuery]);
  const approvedPersonnel = personnelQuery.getResource() as
    | {some: (cb: (record: {getStatus: () => string}) => boolean) => boolean}
    | undefined;
  const canAddCoteacher = Boolean(
    isPersonnelReady && approvedPersonnel?.some((record) => record.getStatus() === 'approved')
  );

  const close = () => setModalToShow('');
  const StudentModal =
    modalToShow && modalToShow !== 'coteacher' ? studentModals[modalToShow] : null;

  return (
    <>
      <TypedManageRosterContext.Provider value={rosterContext}>
        <Menu
          closeOnItemClick
          popperProps={EDGE_MENU_POPPER_PROPS}
          position='bottom-end'
          positionFixed
        >
          <Menu.Trigger>{trigger}</Menu.Trigger>
          <Menu.Content className='[&_.o-menu-content]:p-x2!'>
            <Menu.Item
              className='px-x0-half py-x1'
              padding='none'
              readonly
              textProps={{color: 'secondary', size: 's'}}
            >
              Invite Students:
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: 'binary', iconStyle: 'regular'}}
              onClick={() => setModalToShow('classCode')}
            >
              Display Class Code
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: 'envelope', iconStyle: 'regular'}}
              onClick={() => setModalToShow('email')}
            >
              Invite via Email
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: 'gears', iconStyle: 'regular'}}
              onClick={() => setModalToShow('LMS')}
            >
              Import from LMS
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: ['fab', 'google']}}
              onClick={() => setModalToShow('googleClassroom')}
            >
              Import from Google Classroom
            </Menu.Item>
            {canAddCoteacher && (
              <>
                <Menu.Item
                  className='mt-x2 px-x0-half py-x1'
                  padding='none'
                  readonly
                  textProps={{color: 'secondary', size: 's'}}
                >
                  Invite Teachers
                </Menu.Item>
                <Menu.Item
                  leftIconProps={{icon: 'person-chalkboard', iconStyle: 'regular'}}
                  onClick={() => setModalToShow('coteacher')}
                >
                  Add co-teacher
                </Menu.Item>
              </>
            )}
          </Menu.Content>
        </Menu>
        {StudentModal && <StudentModal classroom={classroom} handleClose={close} />}
      </TypedManageRosterContext.Provider>
      {modalToShow === 'coteacher' && (
        <TypedPeopleContext.Provider
          value={{classroom, queries: {coteachers: getCoteachersQuery(classroomId)}}}
        >
          <AddCoteacherModal handleClose={close} />
        </TypedPeopleContext.Provider>
      )}
    </>
  );
};
